import { type FC, useMemo, useState } from "react";

import { css } from "@emotion/css";
import { Colors } from "@snapchat/snap-design-system";
import { Pie, PieChart, Tooltip } from "@snapchat/snap-design-system-charts";

import { Flexbox } from "../../../shared/components/Flexbox.styles";
import { areStatsEmpty } from "../../derived-data";
import { formatLabelWithPercent } from "../../formatters";
import { type OsStatsItem } from "../../models";
import { LegendItem } from "../audience-tab/LegendItem";
import { LegendVertical } from "../audience-tab/LegendItem.styles";
import { OsTooltip } from "../audience-tab/OsTooltip";
import { StatsEmptyStateContainer } from "../shared";

const pieChart = css`
    margin-left: 40px;
`;

interface PieChartComponentProps {
    loading: boolean;
    metrics: readonly OsStatsItem[];
}

export const PieChartComponent: FC<PieChartComponentProps> = ({ loading, metrics }) => {
    const [activeElement, setActiveElement] = useState<string | null>(null);
    const isEmpty = useMemo(() => areStatsEmpty(metrics), [metrics]);
    const filteredMetrics = metrics.filter((m) => {
        return m.valuePercent !== 0;
    }); // Don't display 0% on UI
    filteredMetrics.sort((a, b) => b.valuePercent - a.valuePercent); // Ensures the values displayed are sorted
    return (
        <StatsEmptyStateContainer isEmpty={isEmpty && !loading}>
            <Flexbox alignItems="flex-start" justifyContent="flex-start">
                <LegendVertical>
                    {filteredMetrics.map(({ valuePercent, label, color = "" }, index: number) => (
                        <LegendItem
                            value={formatLabelWithPercent(valuePercent)}
                            key={index}
                            name={label.includes("UNKNOWN") ? "OTHERS" : label}
                            color={label === activeElement ? Colors.Yellow.V200 : color}
                            loading={loading}
                        />
                    ))}
                </LegendVertical>
                <PieChart width={192} height={192} className={pieChart} nameKey="label">
                    <Pie
                        activeColor={Colors.Yellow.V200}
                        dataKey="value"
                        data={metrics}
                        onSectorMouseEnter={(entity: OsStatsItem) => {
                            setActiveElement(entity.label);
                        }}
                        onSectorMouseLeave={() => {
                            setActiveElement(null);
                        }}
                    />
                    <Tooltip component={<OsTooltip />} />
                </PieChart>
            </Flexbox>
        </StatsEmptyStateContainer>
    );
};
